import { useEffect, useMemo, useState } from "react";
import {
  Check,
  CheckCircle2,
  ChevronRight,
  ChevronsUpDown,
  EyeOff,
  HelpCircle,
  Link2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoaderButton } from "@/components/ui/custom-buttons";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { useToast } from "@/hooks/use-toast";
import { SupportedLocale, toSupportedLocale } from "@/i18n";
import { cn } from "@/lib/utils";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { useTranslation } from "react-i18next";
import {
  ColumnMapEntry,
  extractImportErrorMessage,
  useApplyImportMapping,
  useImportFields,
  useImportMappings,
  useSaveImportMapping,
  useUpdateImportColumns,
} from "@/api/import";
import {
  translateImportApiError,
  translateImportFieldLabel,
} from "@/lib/import/import-server-i18n";
import {
  ImportAnalysedColumn,
  ImportAnalysisReport,
  ImportColumnState,
  ImportTable,
} from "@/lib/import/import-types";

/**
 * Comment chaque colonne du fichier a ete comprise — et, pour celles qui n'ont
 * pas ete reconnues, ce que l'utilisateur decide d'en faire.
 *
 * Principe de toute la fonctionnalite : **aucune ecriture silencieuse**. Une
 * colonne non reconnue n'est donc jamais ignoree d'office : l'utilisateur la
 * rattache a un champ, ou l'ignore explicitement. Tant qu'une colonne reste sans
 * choix, l'import est bloque.
 */
const STATE_LABELS: Record<ImportColumnState, string> = {
  matched: "Reconnue",
  uncertain: "À confirmer",
  ambiguous: "Ambiguë",
  unknown: "Non reconnue",
};

/** Valeur de select signifiant « ignorer explicitement cette colonne ». */
const IGNORE = "__ignore__";

/**
 * Sous-colonnes d'un champ « dimensions » : un champ dimensions est proposé
 * éclaté (Hauteur / Largeur / Profondeur / Diamètre), pour rattacher un fichier
 * qui a une colonne par mesure. Miroir de `DIMENSION_SUB_KEYS` côté API.
 */
const DIMENSION_SUB_KEYS = [
  "cm_height",
  "cm_width",
  "cm_depth",
  "cm_diameter",
] as const;

/**
 * Libellé localisé d'une sous-mesure. Les clés `cm_*` existent déjà dans le
 * bundle i18n (« Height (cm) », « Altura (cm) »…) mais préfixées d'un « - » de
 * mise en forme, qu'on retire ici. Évite de recoder les traductions.
 */
const dimensionSubLabel = (
  t: (key: string) => string,
  subKey: string,
): string => t(subKey).replace(/^\s*[-–]\s*/, "").trim();

const isDimensionSubKey = (suffix: string): boolean =>
  (DIMENSION_SUB_KEYS as readonly string[]).includes(suffix);

const StateBadge = ({ state }: { state: ImportColumnState }) => {
  const vt = useVisibleT();
  const label = vt(`bo.import.columns.state.${state}`, STATE_LABELS[state]);

  if (state === "matched") {
    return (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100 font-normal">
        {label}
      </Badge>
    );
  }
  if (state === "uncertain" || state === "ambiguous") {
    return (
      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 font-normal">
        {label}
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="font-normal text-gray-600">
      {label}
    </Badge>
  );
};

/** Cellule « votre colonne » : intitulé, lettre, remplissage. Réutilisée. */
const YourColumnCell = ({ column }: { column: ImportAnalysedColumn }) => {
  const vt = useVisibleT();
  return (
    <div className="flex flex-col">
      <span className="font-medium">
        {column.header || (
          <span className="text-gray-400 italic">
            {vt("bo.import.columns.noHeader", "(sans intitulé)")}
          </span>
        )}
      </span>
      <span className="text-xs text-gray-500">
        {vt("bo.import.columns.letter", "colonne")} {column.letter}
        {" · "}
        {column.filled} {vt("bo.import.columns.filledRows", "lignes remplies")}
      </span>
    </div>
  );
};

/** Aperçu des premières valeurs de la colonne. Réutilisé. */
const SamplesCell = ({ samples }: { samples: string[] }) => {
  const vt = useVisibleT();
  return (
    <div className="flex flex-wrap gap-1">
      {samples.length === 0 ? (
        <span className="text-xs text-gray-400">
          {vt("bo.import.columns.empty", "vide")}
        </span>
      ) : (
        samples.map((sample) => (
          <Badge
            key={sample}
            variant="secondary"
            className="font-normal max-w-[14rem] truncate"
            title={sample}
          >
            {sample}
          </Badge>
        ))
      )}
    </div>
  );
};

/** Tableau en lecture seule : les colonnes déjà reconnues. */
const ResolvedTable = ({ columns }: { columns: ImportAnalysedColumn[] }) => {
  const vt = useVisibleT();
  const { t } = useTranslation();
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[30%]">
            {vt("bo.import.columns.yourColumn", "Votre colonne")}
          </TableHead>
          <TableHead className="w-[32%]">
            {vt("bo.import.columns.samples", "Exemples de contenu")}
          </TableHead>
          <TableHead className="w-[24%]">
            {vt("bo.import.columns.target", "Champ InventoZen")}
          </TableHead>
          <TableHead className="w-[14%]">
            {vt("bo.import.columns.status", "Statut")}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {columns.map((column) => (
          <TableRow key={column.columnIndex}>
            <TableCell className="align-top">
              <YourColumnCell column={column} />
            </TableCell>
            <TableCell className="align-top">
              <SamplesCell samples={column.samples} />
            </TableCell>
            <TableCell className="align-top">
              <span className="flex items-center gap-1.5">
                {translateImportFieldLabel(
                  t,
                  column.fieldKey,
                  column.fieldLabel ?? "",
                )}
                {column.lang && (
                  <span className="text-xs text-gray-500 uppercase">
                    {column.lang}
                  </span>
                )}
                {column.kind === "relation" && (
                  <Link2 className="w-3.5 h-3.5 text-blue-500" aria-hidden />
                )}
              </span>
            </TableCell>
            <TableCell className="align-top">
              <StateBadge state={column.state} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/**
 * Réutiliser une correspondance d'un import à l'autre : en appliquer une
 * enregistrée (rapprochée par en-tête), ou enregistrer la correspondance
 * courante sous un nom.
 */
const SavedMappings = ({
  report,
  jobId,
  table,
  onApplyEntries,
}: {
  report: ImportAnalysisReport;
  jobId: string;
  table: ImportTable;
  /** pré-remplit les dropdowns « Rattacher à » avec la correspondance appliquée */
  onApplyEntries: (entries: ColumnMapEntry[]) => void;
}) => {
  const vt = useVisibleT();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data: saved } = useImportMappings(table);
  const applyMapping = useApplyImportMapping();
  const saveMapping = useSaveImportMapping(table);
  const [selected, setSelected] = useState<string>("");
  const [name, setName] = useState("");

  const hasMapped = report.mappedColumnCount > 0;
  const hasSaved = (saved ?? []).length > 0;
  if (!hasSaved && !hasMapped) return null;

  const apply = () => {
    if (!selected) return;
    applyMapping.mutate(
      { jobId, mappingId: selected },
      {
        onSuccess: (data) => {
          onApplyEntries(data.entries);
          toast({
            title: vt(
              "bo.import.columns.mappingApplied",
              "Correspondance appliquée",
            ),
            description: `« ${data.name} » — ${data.applied} ${vt(
              "bo.import.columns.columnsMatched",
              "colonne(s) pré-remplie(s), à confirmer",
            )}`,
          });
        },
        onError: (error) => {
          toast({
            variant: "destructive",
            title: translateImportApiError(
              t,
              extractImportErrorMessage(error),
              vt(
                "bo.import.columns.applyError",
                "Impossible d'appliquer la correspondance.",
              ),
            ),
          });
        },
      },
    );
  };

  const save = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    saveMapping.mutate(
      { jobId, name: trimmed },
      {
        onSuccess: (data) => {
          setName("");
          toast({
            title: vt("bo.import.columns.mappingSaved", "Correspondance enregistrée"),
            description: `« ${data.name} » — ${data.columnCount} ${vt(
              "bo.import.columns.columnsMatched",
              "colonne(s) rapprochée(s)",
            )}`,
          });
        },
        onError: (error) => {
          toast({
            variant: "destructive",
            title: translateImportApiError(
              t,
              extractImportErrorMessage(error),
              vt(
                "bo.import.columns.saveMappingError",
                "Impossible d'enregistrer la correspondance.",
              ),
            ),
          });
        },
      },
    );
  };

  return (
    <div className="flex flex-wrap items-end justify-between gap-4 p-3 rounded-xl border bg-gray-50/60">
      {hasSaved ? (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-600">
            {vt("bo.import.columns.reuseTitle", "Réutiliser une correspondance")}
          </span>
          <div className="flex items-center gap-2">
            <Select value={selected || undefined} onValueChange={setSelected}>
              <SelectTrigger className="w-64">
                <SelectValue
                  placeholder={vt("bo.import.columns.reusePick", "Choisir…")}
                />
              </SelectTrigger>
              <SelectContent>
                {(saved ?? []).map((mapping) => (
                  <SelectItem key={mapping.id} value={mapping.id}>
                    {mapping.name}
                    <span className="ml-1.5 text-xs text-gray-400">
                      {mapping.columnCount}{" "}
                      {vt("bo.import.columns.columnsShort", "col.")}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={apply}
              disabled={!selected || applyMapping.isPending}
            >
              {vt("bo.import.columns.apply", "Appliquer")}
            </Button>
          </div>
        </div>
      ) : (
        <span />
      )}

      {hasMapped && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-600">
            {vt("bo.import.columns.saveReuseTitle", "Enregistrer pour réutilisation")}
          </span>
          <div className="flex items-center gap-2">
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={vt("bo.import.columns.mappingName", "Nom (ex. Format mensuel)")}
              className="w-56"
              maxLength={120}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={save}
              disabled={!name.trim() || saveMapping.isPending}
            >
              {vt("bo.import.columns.saveReuse", "Enregistrer")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Cible sélectionnable pour une colonne : un champ, éventuellement une langue
 * précise (les titres sont éclatés par langue active de l'instance).
 */
interface TargetOption {
  /** clé encodée : `${fieldKey}`, `${fieldKey}@${lang}` ou `${fieldKey}@${subKey}` */
  value: string;
  label: string;
  fieldKey: string;
  lang?: SupportedLocale;
  /** sous-clé de dimensions (cm_height…) */
  subKey?: string;
  /** variante d'un champ multilingue (regroupée en sous-menu par champ) */
  multilingual?: boolean;
}

/** Entrée de la liste : un champ simple, ou un champ multilingue (sous-menu). */
type TargetEntry =
  | { type: "flat"; option: TargetOption }
  | {
      type: "group";
      fieldKey: string;
      label: string;
      variants: TargetOption[];
    };

/**
 * Combobox recherchable de rattachement d'une colonne à un champ.
 *
 * Remplace le `Select` : le catalogue compte des centaines de champs, la
 * recherche par nom est indispensable. Les champs multilingues sont regroupés :
 * ils apparaissent une fois, et le survol ouvre un sous-menu latéral listant
 * leurs variantes de langue (retour client).
 */
const ColumnTargetCombobox = ({
  value,
  options,
  candidateKeys,
  onChange,
}: {
  value: string;
  options: TargetOption[];
  candidateKeys: Set<string>;
  onChange: (value: string) => void;
}) => {
  const vt = useVisibleT();
  const [open, setOpen] = useState(false);
  // Champ multilingue survolé : son sous-menu de langues s'affiche à droite.
  const [activeGroup, setActiveGroup] = useState<string | null>(null);

  const selected = options.find((option) => option.value === value);
  const ignoring = value === IGNORE;

  // Entrées : champs simples + parents multilingues (une entrée par champ),
  // suggérés en tête (tri stable).
  const entries = useMemo<TargetEntry[]>(() => {
    const flat: TargetEntry[] = [];
    const groups = new Map<string, Extract<TargetEntry, { type: "group" }>>();
    for (const option of options) {
      if (option.multilingual) {
        const group = groups.get(option.fieldKey) ?? {
          type: "group" as const,
          fieldKey: option.fieldKey,
          label: option.label,
          variants: [],
        };
        group.variants.push(option);
        groups.set(option.fieldKey, group);
      } else {
        flat.push({ type: "flat", option });
      }
    }
    const list: TargetEntry[] = [...flat, ...groups.values()];
    const keyOf = (entry: TargetEntry) =>
      entry.type === "flat" ? entry.option.fieldKey : entry.fieldKey;
    return list.sort(
      (a, b) =>
        (candidateKeys.has(keyOf(a)) ? 0 : 1) -
        (candidateKeys.has(keyOf(b)) ? 0 : 1),
    );
  }, [options, candidateKeys]);

  const activeVariants =
    (activeGroup &&
      entries.find(
        (entry): entry is Extract<TargetEntry, { type: "group" }> =>
          entry.type === "group" && entry.fieldKey === activeGroup,
      )?.variants) ||
    [];

  // Le volet de langues n'existe que quand on survole un champ multilingue.
  const showSubmenu = !!activeGroup && activeVariants.length > 0;

  const triggerLabel = ignoring
    ? vt("bo.import.columns.ignoreColumn", "Ignorer cette colonne")
    : selected
      ? selected.multilingual
        ? `${selected.label} - ${selected.lang?.toUpperCase()}`
        : selected.label
      : vt("bo.import.columns.choose", "À trancher…");

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setActiveGroup(null);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span
            className={cn("truncate", !selected && !ignoring && "text-gray-500")}
          >
            {triggerLabel}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[34rem] max-w-[95vw] p-0">
        <div className="flex">
          <Command
            className={cn("flex-1", showSubmenu && "border-r")}
            filter={(itemValue, search) =>
              itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
            }
          >
            <CommandInput
              placeholder={vt("bo.import.columns.search", "Rechercher un champ…")}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>
                {vt("bo.import.columns.searchEmpty", "Aucun champ ne correspond.")}
              </CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="ignorer ignore"
                  onMouseEnter={() => setActiveGroup(null)}
                  onSelect={() => {
                    onChange(IGNORE);
                    setOpen(false);
                  }}
                >
                  <EyeOff className="mr-2 h-3.5 w-3.5 text-gray-500" aria-hidden />
                  <span className="text-gray-600">
                    {vt("bo.import.columns.ignoreColumn", "Ignorer cette colonne")}
                  </span>
                  <Check
                    className={cn(
                      "ml-auto h-4 w-4",
                      ignoring ? "opacity-100" : "opacity-0",
                    )}
                    aria-hidden
                  />
                </CommandItem>
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                {entries.map((entry) =>
                  entry.type === "flat" ? (
                    <CommandItem
                      key={entry.option.value}
                      value={`${entry.option.label} ${entry.option.fieldKey}`}
                      onMouseEnter={() => setActiveGroup(null)}
                      onSelect={() => {
                        onChange(entry.option.value);
                        setOpen(false);
                      }}
                    >
                      <span className="break-words">{entry.option.label}</span>
                      {candidateKeys.has(entry.option.fieldKey) && (
                        <span className="ml-1.5 shrink-0 text-xs text-blue-600">
                          {vt("bo.import.columns.suggested", "· suggéré")}
                        </span>
                      )}
                      <Check
                        className={cn(
                          "ml-auto mt-0.5 h-4 w-4 shrink-0",
                          value === entry.option.value
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                        aria-hidden
                      />
                    </CommandItem>
                  ) : (
                    <CommandItem
                      key={entry.fieldKey}
                      value={`${entry.label} ${entry.fieldKey}`}
                      onMouseEnter={() => setActiveGroup(entry.fieldKey)}
                      onSelect={() => {
                        // Clic direct = langue principale ; survol = choix précis.
                        onChange(entry.variants[0].value);
                        setOpen(false);
                      }}
                    >
                      <span className="break-words">{entry.label}</span>
                      {candidateKeys.has(entry.fieldKey) && (
                        <span className="ml-1.5 shrink-0 text-xs text-blue-600">
                          {vt("bo.import.columns.suggested", "· suggéré")}
                        </span>
                      )}
                      {entry.variants.some((v) => v.value === value) && (
                        <span className="ml-1.5 shrink-0 text-xs font-medium text-blue-700">
                          {selected?.lang?.toUpperCase()}
                        </span>
                      )}
                      <ChevronRight
                        className="ml-auto mt-0.5 h-4 w-4 shrink-0 text-gray-400"
                        aria-hidden
                      />
                    </CommandItem>
                  ),
                )}
              </CommandGroup>
            </CommandList>
          </Command>

          {/* Sous-menu latéral : affiché UNIQUEMENT au survol d'un champ
              multilingue, jamais autrement. */}
          {showSubmenu && (
            <div className="w-[16rem] shrink-0 overflow-y-auto p-1 max-h-[300px]">
              <div className="px-2 py-1.5 text-xs font-medium text-gray-500">
                {vt("bo.import.columns.language", "Langue")}
              </div>
              {activeVariants.map((variant) => (
                <button
                  key={variant.value}
                  type="button"
                  onClick={() => {
                    onChange(variant.value);
                    setOpen(false);
                  }}
                  className="flex w-full items-start justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-blue-50"
                >
                  <span className="break-words">
                    {variant.label} - {variant.lang?.toUpperCase()}
                  </span>
                  <Check
                    className={cn(
                      "mt-0.5 h-4 w-4 shrink-0",
                      value === variant.value ? "opacity-100" : "opacity-0",
                    )}
                    aria-hidden
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const ImportColumnsReview = ({
  report,
  jobId,
  table,
  onReportChange,
}: {
  report: ImportAnalysisReport;
  jobId: string | null;
  table: ImportTable;
  onReportChange: (report: ImportAnalysisReport) => void;
}) => {
  const vt = useVisibleT();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  // Libellés des champs = catalogue serveur, à demander dans la langue
  // d'interface active. Sans ce `lang`, l'API retombe sur
  // `instance.mainLanguage` : les champs restent en FR alors que le BO est,
  // p. ex., en anglais.
  const lang = toSupportedLocale(i18n.resolvedLanguage ?? i18n.language);
  const { data: fields } = useImportFields(table, lang);
  const updateColumns = useUpdateImportColumns();

  const withHeader = report.columns.filter((column) => column.header);
  // Interactif : tout ce qui n'est pas reconnu automatiquement (y compris ce que
  // l'utilisateur a déjà choisi d'ignorer, pour qu'il puisse revenir dessus).
  const toDecide = withHeader.filter((column) => column.state !== "matched");
  const resolved = withHeader.filter((column) => column.state === "matched");

  // Choix courant par colonne : valeur de cible encodée, IGNORE, ou "" (non
  // tranché). Une valeur encode le champ et, pour un titre, sa langue.
  const [choices, setChoices] = useState<Record<number, string>>({});

  // Langues actives de l'instance : chaque champ multilingue est proposé via un
  // sous-menu listant ces langues (retour client). Repli sur la langue
  // principale si le CMS n'en déclare qu'une.
  const { websiteLanguages } = useCmsWebsiteLanguages();

  // Champs sélectionnables : tous les champs importables. Les dimensions sont
  // proposées éclatées par mesure (cf. `allTargets`), pour rattacher un fichier
  // qui a une colonne Hauteur / Largeur / Profondeur / Diamètre.
  const selectableFields = useMemo(() => fields ?? [], [fields]);

  // Champs multilingues : présentés via un sous-menu (une variante par langue).
  const multilingualFieldKeys = useMemo(
    () =>
      new Set(
        selectableFields
          .filter((field) => !!field.multilingual)
          .map((field) => field.key),
      ),
    [selectableFields],
  );

  /** Encode (champ, langue/sous-clé) en valeur de cible : `key`, `key@lang` ou `key@subKey`. */
  const encodeTarget = (
    fieldKey: string,
    lang?: string | null,
    subKey?: string | null,
  ) => {
    if (subKey) return `${fieldKey}@${subKey}`;
    if (lang && multilingualFieldKeys.has(fieldKey)) return `${fieldKey}@${lang}`;
    return fieldKey;
  };

  /** Décode une valeur de cible en { fieldKey, lang?, subKey? }. */
  const decodeTarget = (
    value: string,
  ): { fieldKey: string; lang?: string; subKey?: string } => {
    const at = value.lastIndexOf("@");
    if (at <= 0) return { fieldKey: value };
    const fieldKey = value.slice(0, at);
    const suffix = value.slice(at + 1);
    return isDimensionSubKey(suffix)
      ? { fieldKey, subKey: suffix }
      : { fieldKey, lang: suffix };
  };

  // Toutes les cibles proposables (identiques pour chaque colonne).
  const allTargets = useMemo<TargetOption[]>(() => {
    const targets: TargetOption[] = [];
    for (const field of selectableFields) {
      // Dimensions : une cible par mesure (Hauteur / Largeur / Profondeur /
      // Diamètre), pour un fichier avec une colonne par mesure.
      if (field.kind === "dimensions") {
        for (const sub of DIMENSION_SUB_KEYS) {
          targets.push({
            value: `${field.key}@${sub}`,
            label: `${field.label} — ${dimensionSubLabel(t, sub)}`,
            fieldKey: field.key,
            subKey: sub,
          });
        }
        continue;
      }
      // Champ multilingue : une variante par langue active, présentées dans un
      // sous-menu au survol (cf. `ColumnTargetCombobox`). Le libellé reste le
      // nom du champ ; la langue est portée par `lang`.
      if (field.multilingual) {
        for (const lang of websiteLanguages) {
          targets.push({
            value: `${field.key}@${lang}`,
            label: field.label,
            fieldKey: field.key,
            lang,
            multilingual: true,
          });
        }
        continue;
      }
      targets.push({
        value: field.key,
        label: field.label,
        fieldKey: field.key,
      });
    }
    return targets;
  }, [selectableFields, websiteLanguages, t]);

  // (Ré)initialise depuis le rapport à chaque recalcul serveur.
  const decideKey = toDecide
    .map(
      (c) =>
        `${c.columnIndex}:${c.fieldKey ?? ""}:${c.lang ?? ""}:${c.subKey ?? ""}:${c.ignored ? "i" : ""}`,
    )
    .join(",");
  useEffect(() => {
    const next: Record<number, string> = {};
    for (const column of toDecide) {
      next[column.columnIndex] = column.ignored
        ? IGNORE
        : column.fieldKey
          ? encodeTarget(column.fieldKey, column.lang, column.subKey)
          : "";
    }
    setChoices(next);
    // decideKey résume l'identité des colonnes à trancher.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decideKey]);

  // Cibles déjà prises par une colonne reconnue : on ne les propose plus.
  const claimedByMatched = useMemo(
    () =>
      new Set(
        resolved
          .filter((column) => column.fieldKey)
          .map((column) =>
            encodeTarget(column.fieldKey as string, column.lang, column.subKey),
          ),
      ),
    // encodeTarget dépend de multilingualFieldKeys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolved, multilingualFieldKeys],
  );

  /** Cibles encore disponibles pour une colonne donnée (hors doublons). */
  const targetsFor = (columnIndex: number): TargetOption[] => {
    const chosenElsewhere = new Set(
      Object.entries(choices)
        .filter(
          ([index, value]) =>
            Number(index) !== columnIndex && value && value !== IGNORE,
        )
        .map(([, value]) => value),
    );
    return allTargets.filter(
      (target) =>
        choices[columnIndex] === target.value ||
        (!claimedByMatched.has(target.value) &&
          !chosenElsewhere.has(target.value)),
    );
  };

  const allDecided = toDecide.every(
    (column) => (choices[column.columnIndex] ?? "") !== "",
  );
  const hasUndecided = !allDecided;

  const ignoreAll = () => {
    setChoices((current) => {
      const next = { ...current };
      for (const column of toDecide) {
        if (!next[column.columnIndex]) next[column.columnIndex] = IGNORE;
      }
      return next;
    });
  };

  /**
   * Applique une correspondance enregistrée en **pré-remplissant** les dropdowns
   * « Rattacher à » des colonnes à trancher (rien n'est écrit tant que
   * l'utilisateur n'a pas cliqué « Enregistrer les correspondances »).
   */
  const applyEntriesToChoices = (entries: ColumnMapEntry[]) => {
    // Robustesse : une API pas encore à jour (ancien format) renvoie un objet
    // sans `entries` — on ne casse pas, on n'a juste rien à pré-remplir.
    const list = Array.isArray(entries) ? entries : [];
    const toDecideIndexes = new Set(toDecide.map((c) => c.columnIndex));
    setChoices((current) => {
      const next = { ...current };
      for (const entry of list) {
        if (!toDecideIndexes.has(entry.columnIndex)) continue;
        next[entry.columnIndex] = entry.fieldKey
          ? encodeTarget(entry.fieldKey, entry.lang, entry.subKey)
          : IGNORE;
      }
      return next;
    });
  };

  const save = () => {
    if (!jobId) return;
    const columns = toDecide.map((column) => {
      const choice = choices[column.columnIndex];
      if (!choice || choice === IGNORE) {
        return { columnIndex: column.columnIndex, fieldKey: null };
      }
      const { fieldKey, lang, subKey } = decodeTarget(choice);
      return {
        columnIndex: column.columnIndex,
        fieldKey,
        ...(lang ? { lang } : {}),
        ...(subKey ? { subKey } : {}),
      };
    });

    updateColumns.mutate(
      { jobId, columns },
      {
        onSuccess: (data) => {
          onReportChange(data.report);
          toast({
            title: vt(
              "bo.import.columns.saved",
              "Correspondances enregistrées.",
            ),
          });
        },
        onError: (error) => {
          toast({
            variant: "destructive",
            title: translateImportApiError(
              t,
              extractImportErrorMessage(error),
              vt(
                "bo.import.columns.saveError",
                "Impossible d'enregistrer les correspondances.",
              ),
            ),
          });
        },
      },
    );
  };

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h3 className="text-base font-semibold">
          {vt("bo.import.columns.title", "Vos colonnes")}
        </h3>
        {toDecide.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-green-800">
            <CheckCircle2 className="w-4 h-4" aria-hidden />
            {vt(
              "bo.import.columns.allRecognized",
              "Toutes vos colonnes ont été reconnues automatiquement.",
            )}
          </p>
        ) : (
          <p className="flex items-center gap-2 text-sm text-amber-800">
            <HelpCircle className="w-4 h-4" aria-hidden />
            {toDecide.length}{" "}
            {toDecide.length > 1
              ? vt(
                  "bo.import.columns.needDecisionPlural",
                  "colonnes n'ont pas été reconnues. Rattachez-les à un champ ou ignorez-les explicitement.",
                )
              : vt(
                  "bo.import.columns.needDecision",
                  "colonne n'a pas été reconnue. Rattachez-la à un champ ou ignorez-la explicitement.",
                )}
          </p>
        )}
      </header>

      {jobId && (
        <SavedMappings
          report={report}
          jobId={jobId}
          table={table}
          onApplyEntries={applyEntriesToChoices}
        />
      )}

      {toDecide.length > 0 && (
        <div className="flex flex-col gap-3">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[26%]">
                  {vt("bo.import.columns.yourColumn", "Votre colonne")}
                </TableHead>
                <TableHead className="w-[28%]">
                  {vt("bo.import.columns.samples", "Exemples de contenu")}
                </TableHead>
                <TableHead className="w-[46%]">
                  {vt("bo.import.columns.mapTo", "Rattacher à…")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {toDecide.map((column) => {
                const candidateKeys = new Set(
                  column.candidates.map((candidate) => candidate.fieldKey),
                );
                return (
                  <TableRow key={column.columnIndex}>
                    <TableCell className="align-top">
                      <YourColumnCell column={column} />
                    </TableCell>
                    <TableCell className="align-top">
                      <SamplesCell samples={column.samples} />
                    </TableCell>
                    <TableCell className="align-top">
                      <ColumnTargetCombobox
                        value={choices[column.columnIndex] || ""}
                        options={targetsFor(column.columnIndex)}
                        candidateKeys={candidateKeys}
                        onChange={(value) =>
                          setChoices((current) => ({
                            ...current,
                            [column.columnIndex]: value,
                          }))
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={ignoreAll}
              disabled={updateColumns.isPending}
            >
              {vt("bo.import.columns.ignoreAll", "Tout ignorer")}
            </Button>
            <div className="flex items-center gap-3">
              {hasUndecided && (
                <span className="text-xs text-amber-700">
                  {vt(
                    "bo.import.columns.decideAll",
                    "Faites un choix pour chaque colonne.",
                  )}
                </span>
              )}
              <LoaderButton
                size="sm"
                onClick={save}
                isLoading={updateColumns.isPending}
                disabled={hasUndecided || updateColumns.isPending || !jobId}
              >
                {vt("bo.import.columns.save", "Enregistrer les correspondances")}
              </LoaderButton>
            </div>
          </div>
        </div>
      )}

      {resolved.length > 0 && (
        <div className="flex flex-col gap-2">
          {/* Colonnes reconnues affichées directement (plus d'accordéon) : toutes
              les colonnes du fichier sont visibles d'un coup. */}
          <h4 className="text-sm font-medium text-gray-700">
            {resolved.length}{" "}
            {resolved.length > 1
              ? vt(
                  "bo.import.columns.recognizedPlural",
                  "colonnes reconnues automatiquement",
                )
              : vt(
                  "bo.import.columns.recognizedSingular",
                  "colonne reconnue automatiquement",
                )}
          </h4>
          <ResolvedTable columns={resolved} />
        </div>
      )}
    </section>
  );
};
